import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';

type Option = { value: string; label: string; count?: number | undefined };
type Props = { options: Option[]; value: string; onChange: (value: string) => void; label: string; fullWidth?: boolean };

/** A segmented switch whose options carry a count, e.g. Waiting 3 · Answered 3. */
export function CountTabs({ options, value, onChange, label, fullWidth = false }: Props) {
  return (
    <Stack direction="row" role="group" aria-label={label} sx={{ p: 0.375, gap: 0.25, borderRadius: 1.25, bgcolor: 'surface.sunken', alignSelf: fullWidth ? 'stretch' : 'flex-start', '& > *': { flex: fullWidth ? 1 : 'none' } }}>
      {options.map((o) => {
        const isOn = o.value === value;
        return (
          <ButtonBase key={o.value} aria-pressed={isOn} onClick={() => { onChange(o.value); }} sx={tabSx(isOn)}>
            {o.label}
            {o.count === undefined ? null : <Box component="span" sx={countSx(isOn)}>{o.count}</Box>}
          </ButtonBase>
        );
      })}
    </Stack>
  );
}

const tabSx = (isOn: boolean) => ({
  gap: 0.75,
  height: 28,
  px: 1.25,
  borderRadius: 1,
  fontSize: '0.75rem',
  fontWeight: isOn ? 600 : 500,
  color: isOn ? 'text.primary' : 'text.secondary',
  bgcolor: isOn ? 'background.paper' : 'transparent',
  boxShadow: isOn ? '0 1px 2px rgba(26, 26, 25, 0.08)' : 'none',
  transition: 'background-color 150ms, color 150ms, box-shadow 150ms',
  '&:hover': { color: 'text.primary' },
});

const countSx = (isOn: boolean) => ({
  minWidth: 18,
  height: 18,
  px: 0.5,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 9,
  fontSize: '0.6875rem',
  fontWeight: 600,
  bgcolor: isOn ? 'primary.main' : 'surface.border',
  color: isOn ? 'primary.contrastText' : 'text.secondary',
  transition: 'background-color 150ms, color 150ms',
});
